import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  useFetchClubPaymentSettingsQuery,
  useUpdateClubPaymentSettingsMutation,
} from "@/api/clubsApi";
import type { ClubPaymentSettingsFull } from "@/api/clubsApi";
import { setTemporaryPaymentSettings } from "@/store/paymentSettingsSlice";
import type { TemporaryPaymentSettings } from "@/store/paymentSettingsSlice";
import { useAppDispatch, useAppSelector } from "@/store";
import type { RootState } from "@/store";
import { Switch } from "@/components/ui/Switch";
import { Icon } from "@/components/ui/Icon";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/AlertDialog";
import { getCurrencySymbol } from "@tinyclub/shared/utils";
import { supabase } from "@/lib/supabase";

interface ClubPaymentSettingsFormProps {
  clubId?: string;
  paymentSettings?: ClubPaymentSettingsFull | null;
  onRefresh?: () => void;
}

export function ClubPaymentSettingsForm({
  clubId,
  paymentSettings,
  onRefresh,
}: ClubPaymentSettingsFormProps) {
  const dispatch = useAppDispatch();
  const temporarySettings = useAppSelector(
    (state: RootState) => state.paymentSettings.temporarySettings
  );

  const isNewClub = !clubId;

  // Fetch existing settings for existing clubs
  const { data: fetchedSettings, isLoading } =
    useFetchClubPaymentSettingsQuery(clubId || "", {
      skip: isNewClub,
    });

  // Use prop if provided, otherwise use fetched data
  const existingSettings = paymentSettings !== undefined ? paymentSettings : fetchedSettings;

  const [updatePaymentSettings] = useUpdateClubPaymentSettingsMutation();

  // Local form state
  const [membershipEnabled, setMembershipEnabled] = useState(false);
  const [showMonthlyPrice, setShowMonthlyPrice] = useState(false);
  const [showYearlyPrice, setShowYearlyPrice] = useState(false);
  const [membershipPriceMonthly, setMembershipPriceMonthly] = useState("");
  const [membershipPriceYearly, setMembershipPriceYearly] = useState("");
  const [membershipTrialDays, setMembershipTrialDays] = useState(0);
  const [accountCurrency, setAccountCurrency] = useState("USD");
  const [deleteConfirm, setDeleteConfirm] = useState<
    "monthly" | "yearly" | null
  >(null);

  const hasInitializedRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Compute Stripe readiness
  const isStripeAccountReady = useMemo(
    () =>
      !!existingSettings?.stripe_account_id &&
      existingSettings?.stripe_account_charges_enabled === true,
    [
      existingSettings?.stripe_account_id,
      existingSettings?.stripe_account_charges_enabled,
    ]
  );

  // Initialize from existing settings or Redux
  useEffect(() => {
    if (hasInitializedRef.current && !isNewClub) {
      return;
    }

    if (isNewClub && temporarySettings) {
      setMembershipEnabled(temporarySettings.membershipEnabled);
      setShowMonthlyPrice(!!temporarySettings.membershipPriceMonthly);
      setShowYearlyPrice(!!temporarySettings.membershipPriceYearly);
      setMembershipPriceMonthly(
        temporarySettings.membershipPriceMonthly?.toString() || ""
      );
      setMembershipPriceYearly(
        temporarySettings.membershipPriceYearly?.toString() || ""
      );
      setMembershipTrialDays(temporarySettings.membershipTrialDays || 0);
      hasInitializedRef.current = true;
    } else if (existingSettings) {
      setMembershipEnabled(existingSettings.membership_enabled || false);
      const monthlyPrice = existingSettings.membership_price_monthly;
      const yearlyPrice = existingSettings.membership_price_yearly;

      setShowMonthlyPrice(!!monthlyPrice);
      setShowYearlyPrice(!!yearlyPrice);
      setMembershipPriceMonthly(
        monthlyPrice ? (monthlyPrice / 100).toString() : ""
      );
      setMembershipPriceYearly(
        yearlyPrice ? (yearlyPrice / 100).toString() : ""
      );
      setMembershipTrialDays(existingSettings.membership_trial_days || 0);
      setAccountCurrency(
        existingSettings.currency?.toUpperCase() || "USD"
      );
      hasInitializedRef.current = true;
    }
  }, [existingSettings, temporarySettings, isNewClub]);

  // Auto-save for existing clubs
  const autoSave = useCallback(async () => {
    if (isNewClub || !clubId) return;

    const settings = {
      club_id: clubId,
      membership_enabled: membershipEnabled,
      membership_price_monthly:
        showMonthlyPrice && membershipPriceMonthly
          ? Math.round(parseFloat(membershipPriceMonthly) * 100)
          : undefined,
      membership_price_yearly:
        showYearlyPrice && membershipPriceYearly
          ? Math.round(parseFloat(membershipPriceYearly) * 100)
          : undefined,
      membership_trial_days: membershipTrialDays,
      currency: accountCurrency.toLowerCase(),
    };

    try {
      await updatePaymentSettings(settings).unwrap();

      // Sync products to Stripe
      await supabase.functions.invoke("sync-club-products", {
        body: { clubId },
      });

      onRefresh?.();
    } catch (error) {
      console.error("[ClubPaymentSettingsForm] Auto-save failed:", error);
    }
  }, [
    isNewClub,
    clubId,
    membershipEnabled,
    showMonthlyPrice,
    showYearlyPrice,
    membershipPriceMonthly,
    membershipPriceYearly,
    membershipTrialDays,
    accountCurrency,
    updatePaymentSettings,
    onRefresh,
  ]);

  // Debounced auto-save trigger
  const triggerAutoSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 1000);
  }, [autoSave]);

  // Update Redux for new clubs
  useEffect(() => {
    if (isNewClub) {
      const settings: TemporaryPaymentSettings = {
        membershipEnabled,
        membershipPriceMonthly:
          showMonthlyPrice && membershipPriceMonthly
            ? parseFloat(membershipPriceMonthly)
            : undefined,
        membershipPriceYearly:
          showYearlyPrice && membershipPriceYearly
            ? parseFloat(membershipPriceYearly)
            : undefined,
        membershipTrialDays,
      };
      dispatch(setTemporaryPaymentSettings(settings));
    }
  }, [
    isNewClub,
    dispatch,
    membershipEnabled,
    showMonthlyPrice,
    showYearlyPrice,
    membershipPriceMonthly,
    membershipPriceYearly,
    membershipTrialDays,
  ]);

  // Trigger auto-save on field changes (existing clubs only)
  useEffect(() => {
    if (!isNewClub && hasInitializedRef.current) {
      triggerAutoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isNewClub,
    membershipEnabled,
    showMonthlyPrice,
    showYearlyPrice,
    membershipPriceMonthly,
    membershipPriceYearly,
    membershipTrialDays,
    accountCurrency,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Price input validation handler
  const handlePriceChange = useCallback(
    (
      value: string,
      setter: (val: string) => void,
      maxValue: number
    ) => {
      if (value === "") {
        setter(value);
        return;
      }
      // Allow valid decimal format (max 2 decimal places)
      const decimalMatch = value.match(/^\d*\.?\d{0,2}$/);
      if (!decimalMatch) return;

      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < maxValue) {
        setter(value);
      }
    },
    []
  );

  // Delete price handlers
  const handleDeleteMonthlyPrice = useCallback(() => {
    if (membershipPriceMonthly && membershipPriceMonthly.trim() !== "") {
      setDeleteConfirm("monthly");
    } else {
      setShowMonthlyPrice(false);
      setMembershipPriceMonthly("");
    }
  }, [membershipPriceMonthly]);

  const handleDeleteYearlyPrice = useCallback(() => {
    if (membershipPriceYearly && membershipPriceYearly.trim() !== "") {
      setDeleteConfirm("yearly");
    } else {
      setShowYearlyPrice(false);
      setMembershipPriceYearly("");
    }
  }, [membershipPriceYearly]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirm === "monthly") {
      setShowMonthlyPrice(false);
      setMembershipPriceMonthly("");
    } else if (deleteConfirm === "yearly") {
      setShowYearlyPrice(false);
      setMembershipPriceYearly("");
    }
    setDeleteConfirm(null);
  }, [deleteConfirm]);

  // Determine if the settings panel should be disabled
  const isDisabled = !isNewClub && !isStripeAccountReady;

  if (!isNewClub && isLoading) {
    return (
      <div className="bg-surface rounded-2xl p-4 flex items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-text-secondary border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <div
        className={`bg-surface rounded-2xl p-4 ${
          isDisabled ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        {/* Toggle row */}
        <div className="flex items-start justify-between">
          <div className="flex-1 mr-4 space-y-2">
            <p className="text-body-1-medium text-text-primary">
              Require paid membership
            </p>
            <p className="text-body-2 text-text-secondary">
              When enabled, only paid members can access this club.
            </p>
          </div>
          <Switch
            checked={membershipEnabled}
            onCheckedChange={setMembershipEnabled}
          />
        </div>

        {/* Pricing section - shown when membership is enabled */}
        {membershipEnabled && (
          <div className="space-y-4">
            <div className="h-px bg-border-subtle mt-4" />

            {/* Monthly recurring */}
            <div>
              <p className="text-body-2-medium text-text-secondary mb-2">
                Monthly recurring
              </p>
              {showMonthlyPrice ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center flex-1 border border-border-tertiary rounded-lg h-11 px-2">
                    <span className="text-body-2 text-text-secondary pl-1">
                      {getCurrencySymbol(accountCurrency)}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="flex-1 bg-transparent text-body-2 text-text-primary pl-1 pr-2 outline-none"
                      value={membershipPriceMonthly}
                      onChange={(e) =>
                        handlePriceChange(
                          e.target.value,
                          setMembershipPriceMonthly,
                          1000
                        )
                      }
                    />
                  </div>
                  <button className="flex items-center border border-border-tertiary rounded-lg h-11 px-3 gap-2">
                    <span className="text-body-2 text-text-primary">
                      {accountCurrency}
                    </span>
                    <Icon
                      name="angle-down"
                      size={14}
                      className="text-text-secondary"
                    />
                  </button>
                  <button onClick={handleDeleteMonthlyPrice}>
                    <Icon
                      name="circle-minus"
                      size={20}
                      className="text-text-secondary"
                    />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowMonthlyPrice(true)}
                  className="w-full bg-surface-1 rounded-lg h-11 flex items-center justify-center gap-2"
                >
                  <Icon
                    name="plus"
                    size={14}
                    className="text-text-secondary"
                  />
                  <span className="text-body-2 text-text-secondary">Add</span>
                </button>
              )}
            </div>

            {/* Yearly recurring */}
            <div>
              <p className="text-body-2-medium text-text-secondary mb-2">
                Yearly recurring
              </p>
              {showYearlyPrice ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center flex-1 border border-border-tertiary rounded-lg h-11 px-2">
                    <span className="text-body-2 text-text-secondary pl-1">
                      {getCurrencySymbol(accountCurrency)}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="flex-1 bg-transparent text-body-2 text-text-primary pl-1 pr-2 outline-none"
                      value={membershipPriceYearly}
                      onChange={(e) =>
                        handlePriceChange(
                          e.target.value,
                          setMembershipPriceYearly,
                          12000
                        )
                      }
                    />
                  </div>
                  <button className="flex items-center border border-border-tertiary rounded-lg h-11 px-3 gap-2">
                    <span className="text-body-2 text-text-primary">
                      {accountCurrency}
                    </span>
                    <Icon
                      name="angle-down"
                      size={14}
                      className="text-text-secondary"
                    />
                  </button>
                  <button onClick={handleDeleteYearlyPrice}>
                    <Icon
                      name="circle-minus"
                      size={20}
                      className="text-text-secondary"
                    />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowYearlyPrice(true)}
                  className="w-full bg-surface-1 rounded-lg h-11 flex items-center justify-center gap-2"
                >
                  <Icon
                    name="plus"
                    size={14}
                    className="text-text-secondary"
                  />
                  <span className="text-body-2 text-text-secondary">Add</span>
                </button>
              )}
            </div>

            {/* Free trial days */}
            <div>
              <p className="text-body-2 text-text-secondary">
                Free trial days
              </p>
              <p className="text-body-3 text-text-tertiary mb-3">
                Members who cancel within this period will not be charged.
              </p>
              <div className="flex items-center gap-2">
                {[
                  { label: "None", value: 0 },
                  { label: "7", value: 7 },
                  { label: "15", value: 15 },
                  { label: "30", value: 30 },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setMembershipTrialDays(option.value)}
                    className={`flex-1 h-11 rounded-lg text-body-2 text-text-primary ${
                      membershipTrialDays === option.value
                        ? "bg-surface-3"
                        : "bg-surface-1"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete price confirmation dialog */}
      <AlertDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {deleteConfirm} price?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {deleteConfirm} subscription option for
              your club.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ClubPaymentSettingsForm;
